import { useId, useMemo, useRef, useState } from 'react';
import { useT } from '../i18n/i18n';
import Celebration from './Celebration';
import { STEP, across, makeLot, pathOf, type Car, type Dir } from './carParkRules';

export type CarParkOptions = { size: number; hints: boolean; motion: boolean };

export const CAR_PARK_DEFAULTS: CarParkOptions = { size: 5, hints: false, motion: true };

export function CarParkOptionsForm({ value, onChange }: { value: CarParkOptions; onChange: (v: CarParkOptions) => void }) {
  const t = useT();
  return (
    <>
      <div className="field">
        <label htmlFor="cp-size">{t('Car park size')}</label>
        <select id="cp-size" value={value.size} onChange={(e) => onChange({ ...value, size: Number(e.target.value) })}>
          <option value={5}>{t('Small — 9 cars')}</option>
          <option value={6}>{t('Medium — about 13 cars')}</option>
          <option value={8}>{t('Large — about 20 cars')}</option>
        </select>
      </div>
      <label className="check">
        <input type="checkbox" checked={value.hints} onChange={(e) => onChange({ ...value, hints: e.target.checked })} />
        {t('Show which cars can drive out')}
      </label>
      <label className="check">
        <input type="checkbox" checked={value.motion} onChange={(e) => onChange({ ...value, motion: e.target.checked })} />
        {t('Moving cars: cars drive away')}
      </label>
    </>
  );
}

const PAINT = ['#e76f51', '#2a9d8f', '#e9b949', '#4a78c2', '#8e6bbf', '#5fa55a', '#e07a9b', '#6b7a8f'];

function mix(hex: string, to: number, amount: number) {
  const n = parseInt(hex.slice(1), 16);
  const ch = [n >> 16, (n >> 8) & 255, n & 255].map((v) => Math.round(v + (to - v) * amount));
  return `rgb(${ch.join(',')})`;
}

const ROTATE: Record<Dir, (len: number) => string> = {
  up: () => '',
  down: (len) => `rotate(180 50 ${len * 50})`,
  right: (len) => `translate(${len * 100} 0) rotate(90)`,
  left: () => 'translate(0 100) rotate(-90)',
};

function CarDrawing({ car, uid }: { car: Car; uid: string }) {
  const h = car.length * 100;
  const paint = PAINT[car.colour];
  const fill = `url(#${uid}-p${car.colour})`;
  const glass = `url(#${uid}-glass)`;
  const sheen = `url(#${uid}-sheen)`;
  const edge = mix(paint, 0, 0.4);
  const tyre = (y: number) => (
    <>
      <rect x="7" y={y} width="9" height="22" rx="3" fill="#15171c" />
      <rect x="84" y={y} width="9" height="22" rx="3" fill="#15171c" />
    </>
  );
  const shell = (top: number, bottom: number) =>
    `M22 ${top + 5} Q50 ${top - 3} 78 ${top + 5} Q88 ${top + 9} 88 ${top + 28} L88 ${bottom - 24} Q88 ${bottom - 3} 75 ${bottom} Q50 ${bottom + 2} 25 ${bottom} Q12 ${bottom - 3} 12 ${bottom - 24} L12 ${top + 28} Q12 ${top + 9} 22 ${top + 5} Z`;
  const lights = (
    <>
      <path d="M17 13 Q21 8 31 8 L30 14 Q22 15 17 17 Z" fill="#fffbe6" stroke="#c9c2a5" strokeWidth="1" />
      <path d="M83 13 Q79 8 69 8 L70 14 Q78 15 83 17 Z" fill="#fffbe6" stroke="#c9c2a5" strokeWidth="1" />
    </>
  );
  const mirrors = (y: number) => (
    <>
      <path d={`M13 ${y} L4 ${y + 3} L5 ${y + 9} L13 ${y + 8} Z`} fill={edge} />
      <path d={`M87 ${y} L96 ${y + 3} L95 ${y + 9} L87 ${y + 8} Z`} fill={edge} />
    </>
  );

  if (car.length > 2) {
    const cab = 92;
    return (
      <svg viewBox={across(car.dir) ? `0 0 ${h} 100` : `0 0 100 ${h}`} preserveAspectRatio="none" aria-hidden="true">
        <g transform={ROTATE[car.dir](car.length)}>
          {tyre(18)}
          {tyre(h - 72)}
          {tyre(h - 44)}
          <path d={shell(4, cab)} fill={fill} stroke={edge} strokeWidth="2" />
          <path d={shell(4, cab)} fill={sheen} />
          <path d="M30 22 Q50 19 70 22" fill="none" stroke={edge} strokeWidth="1.5" opacity="0.6" />
          <path d="M19 30 Q50 22 81 30 L77 52 Q50 47 23 52 Z" fill={glass} />
          <path d="M25 33 Q37 29 50 28 L45 37 Q34 38 27 42 Z" fill="#ffffff" opacity="0.25" />
          <rect x="23" y="54" width="54" height={cab - 62} rx="6" fill={mix(paint, 255, 0.18)} />
          {mirrors(34)}
          {lights}
          <rect x="11" y={cab + 4} width="78" height={h - cab - 8} rx="5" fill="#eef0f2" stroke="#9aa1ab" strokeWidth="2" />
          <rect x="11" y={cab + 4} width="78" height={h - cab - 8} rx="5" fill={sheen} />
          {Array.from({ length: Math.floor((h - cab - 20) / 18) }, (_, i) => (
            <line key={i} x1="16" x2="84" y1={cab + 16 + i * 18} y2={cab + 16 + i * 18} stroke="#c4c9d0" strokeWidth="2" />
          ))}
          <rect x="11" y={cab + 20} width="78" height="10" fill={paint} opacity="0.9" />
          <rect x="16" y={h - 9} width="14" height="5" rx="2" fill="#d9483b" />
          <rect x="70" y={h - 9} width="14" height="5" rx="2" fill="#d9483b" />
        </g>
      </svg>
    );
  }

  return (
    <svg viewBox={across(car.dir) ? `0 0 ${h} 100` : `0 0 100 ${h}`} preserveAspectRatio="none" aria-hidden="true">
      <g transform={ROTATE[car.dir](car.length)}>
        {tyre(24)}
        {tyre(h - 50)}
        <path d={shell(4, h - 4)} fill={fill} stroke={edge} strokeWidth="2" />
        <path d={shell(4, h - 4)} fill={sheen} />
        <path d="M32 14 Q34 26 33 38 M68 14 Q66 26 67 38" fill="none" stroke={edge} strokeWidth="1.5" opacity="0.55" />
        <path d="M19 44 Q50 36 81 44 L76 70 Q50 65 24 70 Z" fill={glass} />
        <path d="M25 47 Q37 42 50 41 L44 51 Q33 52 27 57 Z" fill="#ffffff" opacity="0.25" />
        <path d={`M24 72 Q50 68 76 72 L76 ${h - 74} Q50 ${h - 70} 24 ${h - 74} Z`} fill={mix(paint, 255, 0.12)} stroke={edge} strokeWidth="1" strokeOpacity="0.5" />
        <path d={`M20 70 L23 72 L23 ${h - 74} L19 ${h - 70} Q16 ${(h) / 2} 20 70 Z`} fill={glass} />
        <path d={`M80 70 L77 72 L77 ${h - 74} L81 ${h - 70} Q84 ${h / 2} 80 70 Z`} fill={glass} />
        <path d={`M24 ${h - 72} Q50 ${h - 68} 76 ${h - 72} L79 ${h - 54} Q50 ${h - 48} 21 ${h - 54} Z`} fill={glass} />
        <line x1="13" x2="19" y1={h / 2 + 4} y2={h / 2 + 4} stroke={edge} strokeWidth="1.5" />
        <line x1="81" x2="87" y1={h / 2 + 4} y2={h / 2 + 4} stroke={edge} strokeWidth="1.5" />
        {mirrors(46)}
        {lights}
        <path d={`M17 ${h - 15} Q20 ${h - 9} 30 ${h - 8} L30 ${h - 13} Q22 ${h - 14} 17 ${h - 19} Z`} fill="#d9483b" />
        <path d={`M83 ${h - 15} Q80 ${h - 9} 70 ${h - 8} L70 ${h - 13} Q78 ${h - 14} 83 ${h - 19} Z`} fill="#d9483b" />
      </g>
    </svg>
  );
}

export default function CarPark({ options, onAgain }: { options: CarParkOptions; onAgain: () => void }) {
  const t = useT();
  const uid = useId().replace(/:/g, '');
  const names: Record<Dir, string> = { up: t('Car facing up'), down: t('Car facing down'), left: t('Car facing left'), right: t('Car facing right') };
  const [lot] = useState(() => makeLot(options.size));
  const [parked, setParked] = useState<Car[]>(lot.cars);
  const [leaving, setLeaving] = useState<Car[]>([]);
  const [moves, setMoves] = useState(0);
  const [blocked, setBlocked] = useState(false);
  const els = useRef(new Map<number, HTMLButtonElement>());
  const done = parked.length === 0;
  const free = useMemo(() => new Set(parked.filter((c) => pathOf(lot, parked, c).blocker === null).map((c) => c.id)), [lot, parked]);

  const shift = (car: Car, cells: number) => {
    const [dr, dc] = STEP[car.dir];
    const pct = (cells / car.length) * 100;
    return across(car.dir) ? `translateX(${dc * pct}%)` : `translateY(${dr * pct}%)`;
  };

  function tap(car: Car) {
    if (done) return;
    const path = pathOf(lot, parked, car);
    const el = els.current.get(car.id);
    setMoves((m) => m + 1);
    if (path.blocker === null) {
      setBlocked(false);
      setParked((p) => p.filter((c) => c.id !== car.id));
      if (!options.motion || !el) return;
      setLeaving((l) => [...l, car]);
      const cells = path.out + car.length + 2;
      const run = el.animate(
        [{ transform: 'none', opacity: 1 }, { transform: shift(car, cells * 0.85), opacity: 1, offset: 0.8 }, { transform: shift(car, cells), opacity: 0 }],
        { duration: 260 + cells * 55, easing: 'cubic-bezier(.5,0,.85,.55)', fill: 'forwards' },
      );
      run.onfinish = () => setLeaving((l) => l.filter((c) => c.id !== car.id));
      return;
    }
    setBlocked(true);
    if (!options.motion || !el) return;
    const reach = path.free + 0.14;
    el.animate(
      [{ transform: 'none' }, { transform: shift(car, reach), offset: 0.55 }, { transform: 'none' }],
      { duration: 280 + path.free * 90, easing: 'ease-in-out' },
    );
    const hit = els.current.get(path.blocker);
    const other = parked.find((c) => c.id === path.blocker);
    if (hit && other) {
      const [dr, dc] = STEP[car.dir];
      hit.animate(
        [{ transform: 'none' }, { transform: `translate(${dc * 3}px, ${dr * 3}px)` }, { transform: 'none' }],
        { duration: 160, delay: (280 + path.free * 90) * 0.5, easing: 'ease-out' },
      );
    }
  }

  const place = (car: Car) => {
    const h = across(car.dir);
    return {
      left: `${(car.col / lot.cols) * 100}%`,
      top: `${(car.row / lot.rows) * 100}%`,
      width: `${((h ? car.length : 1) / lot.cols) * 100}%`,
      height: `${((h ? 1 : car.length) / lot.rows) * 100}%`,
    };
  };

  const shown = [...parked, ...leaving].sort((a, b) => a.id - b.id);

  return (
    <div className="lz-carpark">
      <p className="lz-status" aria-live="polite">
        {done ? t('The car park is clear. You used {n} moves.', { n: moves }) : t('Tap a car to drive it out. Cars left: {n}', { n: parked.length })}
        <span className="lz-score">{t('Moves: {n}', { n: moves })}</span>
      </p>
      <p className="lz-message" role="status">{blocked && !done ? t('That car is blocked. Move the car in front of it first.') : ' '}</p>
      <div className="lz-cp-stage">
        {done && <Celebration text={t('Car park cleared!')} motion={options.motion} />}
        <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
          <defs>
            {PAINT.map((p, i) => (
              <linearGradient key={i} id={`${uid}-p${i}`} x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor={mix(p, 0, 0.28)} />
                <stop offset="0.22" stopColor={p} />
                <stop offset="0.5" stopColor={mix(p, 255, 0.28)} />
                <stop offset="0.78" stopColor={p} />
                <stop offset="1" stopColor={mix(p, 0, 0.28)} />
              </linearGradient>
            ))}
            <linearGradient id={`${uid}-glass`} x1="0" x2="0.4" y1="0" y2="1">
              <stop offset="0" stopColor="#4a5d72" />
              <stop offset="0.55" stopColor="#1c2632" />
              <stop offset="1" stopColor="#111821" />
            </linearGradient>
            <linearGradient id={`${uid}-sheen`} x1="0" x2="0.6" y1="0" y2="1">
              <stop offset="0" stopColor="#ffffff" stopOpacity="0.35" />
              <stop offset="0.35" stopColor="#ffffff" stopOpacity="0.05" />
              <stop offset="0.7" stopColor="#000000" stopOpacity="0" />
              <stop offset="1" stopColor="#000000" stopOpacity="0.18" />
            </linearGradient>
          </defs>
        </svg>
        <div className="lz-cp-road">
          <div className="lz-cp-lot" style={{ aspectRatio: `${lot.cols} / ${lot.rows}`, backgroundSize: `${100 / lot.cols}% ${100 / lot.rows}%` }} role="group" aria-label={t('Car park')}>
            {shown.map((car) => {
              const gone = !parked.includes(car);
              return (
                <button
                  key={car.id}
                  ref={(el) => { if (el) els.current.set(car.id, el); else els.current.delete(car.id); }}
                  type="button"
                  className={`lz-cp-car${options.hints && free.has(car.id) ? ' is-free' : ''}`}
                  style={place(car)}
                  disabled={gone}
                  aria-label={names[car.dir]}
                  onClick={() => tap(car)}
                >
                  <CarDrawing car={car} uid={uid} />
                </button>
              );
            })}
          </div>
        </div>
      </div>
      <div className="toolbar lz-actions">
        <button type="button" className={done ? 'btn' : 'btn secondary'} onClick={onAgain}>{done ? t('Play again') : t('New car park')}</button>
      </div>
    </div>
  );
}
